"use client";

import * as Sentry from "@sentry/nextjs";
import { useEffect } from "react";
import Link from "next/link";
import { AlertTriangle, Home, RefreshCcw } from "lucide-react";
import translations from "@/translations/ar.json";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  const t = translations.errors["500"];

  return (
    <html lang="ar" dir="rtl">
      <body className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-950 dark:to-gray-900">
        <div className="flex min-h-screen items-center justify-center px-4 py-12">
          <div className="w-full max-w-md text-center">
            {/* Error Icon */}
            <div className="mx-auto mb-8 flex h-20 w-20 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
              <AlertTriangle className="h-10 w-10 text-red-600 dark:text-red-500" />
            </div>

            {/* Error Code */}
            <div className="mb-4">
              <span className="text-6xl font-bold text-red-600 dark:text-red-500">
                {t.code}
              </span>
            </div>

            {/* Error Title */}
            <h1 className="mb-4 text-2xl font-bold text-gray-900 dark:text-gray-100">
              {t.heading}
            </h1>

            {/* Error Description */}
            <p className="mb-8 text-gray-600 dark:text-gray-400">
              {t.description}
            </p>

            {/* Error Digest (if available) */}
            {error.digest && (
              <div className="mb-8 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-900/50">
                <p className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t.error_id}
                </p>
                <code className="text-xs text-gray-500 dark:text-gray-400">
                  {error.digest}
                </code>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button
                onClick={reset}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-6 py-3 font-medium text-white transition-colors hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800"
              >
                <RefreshCcw className="h-4 w-4" />
                {t.actions.retry}
              </button>

              <Link
                href="/"
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-6 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <Home className="h-4 w-4" />
                {t.actions.home}
              </Link>
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
